import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Repeat,
  TrendingUp,
  Bot,
  Target,
  Wallet,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
  Star,
  Activity,
  BarChart3,
  Layers,
  Info,
  Globe,
  Bell,
  Shield,
  Zap,
  DollarSign,
  Bitcoin,
  Eye,
  EyeOff,
  Gift,
  CreditCard
} from 'lucide-react';
import { TradingMode, UserStatus } from '../App';
import { MarketData } from '../hooks/useDatabase';
import { User as UserType } from '@supabase/supabase-js';
import { useMarketData } from '../contexts/MarketDataContext';
import { useBybitData } from '../contexts/BybitDataContext';
import { CurrencySelector, useDisplayCurrency } from '../contexts/DisplayCurrencyContext';
import { TOP_CRYPTO_PAIRS } from '../constants/tradingPairs';



interface HeaderProps {
  tradingMode: TradingMode;
  setTradingMode: (mode: TradingMode) => void;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  currentPrice: number;
  marketData?: MarketData;
  usdtBalance: number;
  btcBalance: number;
  totalPortfolioValue: number;
  user: UserType;
  signOut: () => void;
  marketDataList: MarketData[];
  userStatus: UserStatus;
  isDemoAccount: boolean;
}

const Header: React.FC<HeaderProps> = ({
  tradingMode,
  setTradingMode,
  selectedPair,
  setSelectedPair,
  currentPrice,
  marketData: selectedMarketData,
  usdtBalance,
  btcBalance,
  totalPortfolioValue,
  user,
  signOut,
  marketDataList,
  userStatus,
  isDemoAccount
}) => {
  const { formatAmount } = useDisplayCurrency();
  const { t } = useTranslation();
  const { marketData: cfdMarketData, isConnected: cfdConnected, getPriceBySymbol: getCfdPrice } = useMarketData();
  const { getPriceBySymbol: getCryptoPrice, isConnected: cryptoConnected } = useBybitData();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getPriceBySymbol = (symbol: string): number => {
    if (tradingMode === 'futures') {
      return getCryptoPrice(symbol);
    }
    return getCfdPrice(symbol);
  };

  const marketData = tradingMode === 'futures'
    ? TOP_CRYPTO_PAIRS.map(pair => ({
        symbol: pair.symbol,
        price: getCryptoPrice(pair.symbol),
        name: pair.name,
        type: pair.type
      }))
    : cfdMarketData;
  const [showBalances, setShowBalances] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPairSelector, setShowPairSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Refs for dropdown handling
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const pairSelectorRef = useRef<HTMLDivElement>(null);

  // Get current price for selected pair
  const getCurrentPairPrice = () => {
    // Use the MarketDataContext's getPriceBySymbol which handles symbol conversion
    const price = getPriceBySymbol(selectedPair);
    if (price > 0) return price;
    
    // Fallback to currentPrice prop
    return currentPrice || 0;
  };

  const currentPairPrice = getCurrentPairPrice();
  const currentBtcPrice = getPriceBySymbol('BTCUSDT') || 0;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (pairSelectorRef.current && !pairSelectorRef.current.contains(event.target as Node)) {
        setShowPairSelector(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigation items
  const navigationItems = [
    { key: 'home', label: t('navigation.home'), icon: Home },
    { key: 'swap', label: t('trading.swap'), icon: Repeat },
    { key: 'futures', label: t('trading.futures'), icon: TrendingUp },
    { key: 'cfd', label: t('trading.cfd'), icon: BarChart3 },
    { key: 'prop_firm', label: t('propFirm.prop'), icon: Target },
    { key: 'robot', label: t('trading.robot'), icon: Bot },
    { key: 'events', label: t('trading.events'), icon: Activity },
    { key: 'staking', label: t('trading.staking'), icon: Layers },
    { key: 'wheel', label: 'Spin Wheel', icon: Gift }
  ];

  // Get status color based on user tier
  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case 'No-Coiner': return 'text-slate-500';
      case 'Shrimp': return 'text-gray-400';
      case 'Crab': return 'text-orange-400';
      case 'Octopus': return 'text-purple-400';
      case 'Dolphin': return 'text-blue-400';
      case 'Shark': return 'text-red-400';
      case 'Whale': return 'text-green-400';
      case 'Humpback': return 'text-cyan-400';
      default: return 'text-slate-400';
    }
  };

  // Get status icon based on user tier
  const getStatusIcon = (status: UserStatus) => {
    switch (status) {
      case 'No-Coiner': return '🚫';
      case 'Shrimp': return '🦐';
      case 'Crab': return '🦀';
      case 'Octopus': return '🐙';
      case 'Dolphin': return '🐬';
      case 'Shark': return '🦈';
      case 'Whale': return '🐋';
      case 'Humpback': return '🐳';
      default: return '📊';
    }
  };

  // Format pair display
  const formatPairDisplay = (symbol: string) => {
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    return symbol;
  };

  // Get appropriate decimal places based on asset type
  const getDecimalPlaces = (symbol: string, price: number) => {
    // Crypto pairs - 2-8 decimals based on price
    if (symbol.endsWith('USDT')) {
      if (price < 0.01) return 8;
      if (price < 1) return 6;
      if (price < 10) return 4;
      return 2;
    }

    // Forex pairs - 5 decimals
    if (symbol.includes('/') && !symbol.includes('XAU') && !symbol.includes('XAG') && !symbol.includes('XPT') && !symbol.includes('XPD')) {
      return 5;
    }

    // Commodities (Gold, Silver, Oil, etc.) - 3 decimals
    if (symbol.includes('XAU') || symbol.includes('XAG') || symbol.includes('XPT') || symbol.includes('XPD') ||
        symbol.includes('NATGAS') || symbol.includes('BCO') || symbol.includes('WTICO') ||
        symbol.includes('CORN') || symbol.includes('WHEAT') || symbol.includes('SOYBN') ||
        symbol.includes('SUGAR') || symbol.includes('COFFEE') || symbol.includes('COTTON') ||
        symbol.includes('COCOA')) {
      return 3;
    }

    // Stocks - 3 decimals
    return 3;
  };

  // Format price with appropriate decimal places
  const formatPrice = (symbol: string, price: number) => {
    const decimals = getDecimalPlaces(symbol, price);
    return price.toFixed(decimals);
  };

  // Filter pairs based on search term
  const getFilteredPairs = () => {
    // For CFD mode, use only the hardcoded CFD instruments
    if (tradingMode === 'cfd') {
      const HARDCODED_CFD_INSTRUMENTS = [
        // Forex Pairs
        { symbol: 'EUR/USD', name: 'Euro/US Dollar', type: 'forex' },
        { symbol: 'GBP/USD', name: 'British Pound/US Dollar', type: 'forex' },
        { symbol: 'AUD/USD', name: 'Australian Dollar/US Dollar', type: 'forex' },
        { symbol: 'NZD/USD', name: 'New Zealand Dollar/US Dollar', type: 'forex' },
        { symbol: 'EUR/JPY', name: 'Euro/Japanese Yen', type: 'forex' },
        { symbol: 'GBP/JPY', name: 'British Pound/Japanese Yen', type: 'forex' },
        { symbol: 'EUR/GBP', name: 'Euro/British Pound', type: 'forex' },
        { symbol: 'EUR/AUD', name: 'Euro/Australian Dollar', type: 'forex' },
        { symbol: 'EUR/CHF', name: 'Euro/Swiss Franc', type: 'forex' },
        { symbol: 'GBP/AUD', name: 'British Pound/Australian Dollar', type: 'forex' },
        { symbol: 'GBP/CHF', name: 'British Pound/Swiss Franc', type: 'forex' },
        { symbol: 'AUD/CAD', name: 'Australian Dollar/Canadian Dollar', type: 'forex' },
        { symbol: 'AUD/CHF', name: 'Australian Dollar/Swiss Franc', type: 'forex' },
        { symbol: 'AUD/JPY', name: 'Australian Dollar/Japanese Yen', type: 'forex' },
        { symbol: 'CAD/JPY', name: 'Canadian Dollar/Japanese Yen', type: 'forex' },
        { symbol: 'CHF/JPY', name: 'Swiss Franc/Japanese Yen', type: 'forex' },
        { symbol: 'NZD/JPY', name: 'New Zealand Dollar/Japanese Yen', type: 'forex' },

        // Commodities
        { symbol: 'XAG/USD', name: 'Silver/USD', type: 'commodity' },
        { symbol: 'NATGAS/USD', name: 'Natural Gas', type: 'commodity' },
        { symbol: 'BCO/USD', name: 'Brent Crude', type: 'commodity' },
        { symbol: 'WTICO/USD', name: 'WTI Crude Oil', type: 'commodity' },
        { symbol: 'XPT/USD', name: 'Platinum/USD', type: 'commodity' },
        { symbol: 'XAU/USD', name: 'Gold/USD', type: 'commodity' },
        { symbol: 'XPD/USD', name: 'Palladium/USD', type: 'commodity' },
        { symbol: 'CORN/USD', name: 'Corn', type: 'commodity' },
        { symbol: 'WHEAT/USD', name: 'Wheat', type: 'commodity' },
        { symbol: 'SOYBN/USD', name: 'Soybean', type: 'commodity' },
        { symbol: 'SUGAR/USD', name: 'Sugar', type: 'commodity' },
        { symbol: 'COFFEE/USD', name: 'Coffee', type: 'commodity' },
        { symbol: 'COTTON/USD', name: 'Cotton', type: 'commodity' },
        { symbol: 'COCOA/USD', name: 'Cocoa', type: 'commodity' },

        // Stocks
        { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' },
        { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock' },
        { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'stock' },
        { symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'stock' },
        { symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock' },
        { symbol: 'META', name: 'Meta Platforms Inc.', type: 'stock' },
        { symbol: 'NFLX', name: 'Netflix Inc.', type: 'stock' },
        { symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock' },
        { symbol: 'BA', name: 'Boeing Company', type: 'stock' },
        { symbol: 'JPM', name: 'JPMorgan Chase & Co.', type: 'stock' },
        { symbol: 'GS', name: 'Goldman Sachs', type: 'stock' },
        { symbol: 'BAC', name: 'Bank of America', type: 'stock' },
        { symbol: 'WMT', name: 'Walmart', type: 'stock' },
        { symbol: 'DIS', name: 'Disney', type: 'stock' },
        { symbol: 'CVX', name: 'Chevron', type: 'stock' }
      ];
      
      if (!searchTerm) {
        return HARDCODED_CFD_INSTRUMENTS.map(item => ({
          ...item,
          price: getCfdPrice(item.symbol)
        }));
      }

      const term = searchTerm.toLowerCase();
      return HARDCODED_CFD_INSTRUMENTS
        .filter(data =>
          data.symbol.toLowerCase().includes(term) ||
          data.name.toLowerCase().includes(term)
        )
        .map(item => ({
          ...item,
          price: getCfdPrice(item.symbol)
        }));
    }
    
    if (!searchTerm) {
      if (tradingMode === 'futures') {
        return TOP_CRYPTO_PAIRS.map(pair => ({
          symbol: pair.symbol,
          price: getCryptoPrice(pair.symbol),
          name: pair.name,
          type: pair.type
        }));
      }
      return cfdMarketData;
    }

    const term = searchTerm.toLowerCase();

    if (tradingMode === 'futures') {
      return TOP_CRYPTO_PAIRS
        .filter(pair =>
          pair.symbol.toLowerCase().includes(term) ||
          pair.name.toLowerCase().includes(term) ||
          formatPairDisplay(pair.symbol).toLowerCase().includes(term)
        )
        .map(pair => ({
          symbol: pair.symbol,
          price: getCryptoPrice(pair.symbol),
          name: pair.name,
          type: pair.type
        }));
    }
    
    // For other modes, apply search to all market data
    return marketData.filter(data => 
      data.symbol.toLowerCase().includes(term) ||
      formatPairDisplay(data.symbol).toLowerCase().includes(term)
    );
  };

 return (
  <header className="bg-slate-800/95 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-40 shadow-xl">
    <div className="px-4 lg:px-8">
      <div className="flex items-center justify-between h-16">

        {/* Left Section - Logo and Navigation */}
        <div className="flex items-center gap-6">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
              >
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hidden sm:block">
              WhiteRock24
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => setTradingMode(item.key as TradingMode)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    tradingMode === item.key
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Center Section - Pair Selector and Price */}
        {(tradingMode === 'futures' || tradingMode === 'cfd' || tradingMode === 'prop_firm') && (
          <div className="hidden lg:flex items-center gap-4">
            {/* Pair Selector */}
            <div className="relative" ref={pairSelectorRef}>
              <button
                onClick={() => setShowPairSelector(!showPairSelector)}
                className="flex items-center gap-2 bg-slate-700/50 hover:bg-slate-600/50 px-4 py-2 rounded-xl border border-slate-600/50 hover:border-slate-500/50 transition-all"
              >
                <span className="text-white font-semibold">{formatPairDisplay(selectedPair)}</span>
                <ChevronDown size={16} className="text-slate-400" />
              </button>
              {showPairSelector && (
                <div className="absolute top-full left-0 mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 w-80 overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-slate-700">
                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                      />
                      <input
                        type="text"
                        placeholder="Search pairs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-900 text-white pl-10 pr-4 py-2 rounded-lg border border-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Pairs List */}
                  <div className="max-h-64 overflow-y-auto">
                    {getFilteredPairs().map((data) => (
                      <button
                        key={data.symbol}
                        onClick={() => {
                          setSelectedPair(data.symbol);
                          setShowPairSelector(false);
                          setSearchTerm('');
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-700 transition-colors flex items-center justify-between ${
                          selectedPair === data.symbol ? 'bg-slate-700 text-blue-400' : 'text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Star size={14} className="text-slate-600" />
                          <span className="font-medium">{formatPairDisplay(data.symbol)}</span>
                        </div>
                        <span className="text-slate-400 font-mono text-sm">
                          ${formatPrice(data.symbol, data.price || 0)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Current Price */}
            <div className="px-4 py-3 rounded-xl border border-slate-600/50 bg-slate-800 text-white flex items-center justify-between w-fit">
              <div className="flex items-center gap-3">
                <span className="font-medium text-sm">{t('header.price')}</span>
              </div>
              <span className="font-mono text-sm ml-3">${formatPrice(selectedPair, currentPairPrice)}</span>
            </div>
          </div>
        )}

        {/* Right Section - Portfolio + User Status + Menu */}
        <div className="flex items-center gap-4">
          {/* Portfolio Value + Currency Dropdown */}
          <div className="hidden md:flex items-center gap-4">
            <div className="px-3 py-2 rounded-xl border border-slate-600/50 bg-slate-800 text-white flex shrink-0 items-center justify-between gap-4 w-fit">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBalances(!showBalances)}
                  className="shrink-0 text-slate-400 hover:text-white transition-colors"
                >
                  {showBalances ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <div>
                  <div className="text-slate-400 text-[10px] leading-4">{t('header.portfolioValue')}</div>
                  <div className="text-white font-mono text-xs leading-4 tabular-nums whitespace-nowrap">
                    {showBalances
                      ? formatAmount(totalPortfolioValue)
                      : '••••••'}
                  </div>
                </div>
              </div>

              <CurrencySelector />
            </div>

            {/* Account Type Indicator */}
            <div className={`px-3 py-2 rounded-xl border ${
              isDemoAccount
                ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-green-500/10 border-green-500/30'
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  isDemoAccount ? 'bg-amber-400' : 'bg-green-400'
                }`} />
                <span className={`text-xs font-semibold ${
                  isDemoAccount ? 'text-amber-400' : 'text-green-400'
                }`}>
                  {isDemoAccount ? 'DEMO' : 'LIVE'}
                </span>
              </div>
            </div>
          </div>

          {/* User Status Badge */}
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
              userStatus === 'Whale' || userStatus === 'Humpback'
                ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30'
                : userStatus === 'Shark'
                ? 'bg-gradient-to-r from-red-500/20 to-pink-500/20 border-red-500/30'
                : userStatus === 'Dolphin'
                ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/30'
                : userStatus === 'Octopus'
                ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border-purple-500/30'
                : userStatus === 'Crab'
                ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/20 border-orange-500/30'
                : 'bg-slate-700/50 border-slate-600/50'
            }`}
          >
            <span className="text-lg">{getStatusIcon(userStatus)}</span>
            <span className={`text-sm font-medium ${getStatusColor(userStatus)}`}>
              {t(`header.tiers.${userStatus.toLowerCase()}`)}
            </span>
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700/50"
            >
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
              <ChevronDown size={16} className="hidden sm:block" />
            </button>

            {showUserMenu && (
              <div className="absolute top-full right-0 mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 w-64 overflow-hidden">
                <div className="p-4 border-b border-slate-700">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="text-white font-medium">{user.email}</div>
                      <div className={`text-xs ${getStatusColor(userStatus)}`}>
                        {getStatusIcon(userStatus)} {userStatus}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-2">
                  <button
                    onClick={() => {
                      setTradingMode('wallet');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-3 text-slate-300 hover:text-white"
                  >
                    <Wallet size={16} />
                    <span>{t('navigation.wallet')}</span>
                  </button>

                  <button
                    onClick={() => {
                      setTradingMode('profile');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-3 text-slate-300 hover:text-white"
                  >
                    <User size={16} />
                    <span>{t('navigation.profile')}</span>
                  </button>

                  <button
                    onClick={() => {
                      signOut();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-3 text-slate-300 hover:text-white"
                  >
                    <LogOut size={16} />
                    <span>{t('navigation.signOut')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700/50"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </div>

    {/* Mobile Menu */}
    {isMobileMenuOpen && (
      <div className="lg:hidden border-t border-slate-700/50 py-4">
        <div className="mb-3"><CurrencySelector /></div>
        {/* Mobile Portfolio Value */}
        <div className="mb-4 bg-slate-700/50 rounded-xl p-4 border border-slate-600/50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-slate-400 text-xs">{t('header.portfolioValue')}</div>
              <div className="text-white font-mono text-lg">
                {showBalances
                  ? formatAmount(totalPortfolioValue)
                  : '••••••'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBalances(!showBalances)}
                className="text-slate-400 hover:text-white transition-colors p-1"
              >
                {showBalances ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${getStatusColor(userStatus)}`}>
                <span>{getStatusIcon(userStatus)}</span>
                <span className="text-xs font-medium">{userStatus}</span>
              </div>
            </div>
          </div>

          {/* Account Type Indicator - Mobile */}
          <div className="flex items-center justify-center">
            <div className={`px-4 py-2 rounded-lg border ${
              isDemoAccount
                ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-green-500/10 border-green-500/30'
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  isDemoAccount ? 'bg-amber-400' : 'bg-green-400'
                }`} />
                <span className={`text-sm font-bold ${
                  isDemoAccount ? 'text-amber-400' : 'text-green-400'
                }`}>
                  {isDemoAccount ? 'DEMO ACCOUNT' : 'LIVE ACCOUNT'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="grid grid-cols-2 gap-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                onClick={() => {
                  setTradingMode(item.key as TradingMode);
                  setIsMobileMenuOpen(false);
                }}
                className={`flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                  tradingMode === item.key
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-600/30'
                }`}
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Mobile Pair Selector */}
        {(tradingMode === 'futures' || tradingMode === 'cfd' || tradingMode === 'prop_firm') && (
          <div className="mt-4 pt-4 border-t border-slate-700/50" ref={pairSelectorRef}>
            <div className="mb-2">
              <div className="text-slate-400 text-xs mb-2">Current Pair</div>
              <button
                onClick={() => setShowPairSelector(!showPairSelector)}
                className="w-full flex items-center justify-between bg-slate-700/50 px-4 py-3 rounded-xl border border-slate-600/50 hover:bg-slate-600 transition-all"
              >
                <span className="text-white font-semibold">{formatPairDisplay(selectedPair)}</span>
                <ChevronDown size={16} className="text-slate-400" />
              </button>

              {showPairSelector && (
                <div className="mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 w-full overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-slate-700">
                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                      />
                      <input
                        type="text"
                        placeholder="Search pairs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-900 text-white pl-10 pr-4 py-2 rounded-lg border border-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Pairs List */}
                  <div className="max-h-64 overflow-y-auto">
                    {getFilteredPairs().map((data) => (
                      <button
                        key={data.symbol}
                        onClick={() => {
                          setSelectedPair(data.symbol);
                          setShowPairSelector(false);
                          setSearchTerm('');
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-700 transition-colors flex items-center justify-between ${
                          selectedPair === data.symbol ? 'bg-slate-700 text-blue-400' : 'text-white'
                        }`}
                      >
                        <span className="font-medium">{formatPairDisplay(data.symbol)}</span>
                        <span className="text-slate-400 font-mono text-sm">
                          ${formatPrice(data.symbol, data.price || 0)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )}
  </header>
   
);
};


  export default Header;
